import { TabContent } from 'components/ui/TabList/TabContent';
import { TabList } from 'components/ui/TabList/TabList';
import { ServiceTypes } from 'constants/ServiceTypes';
import { History } from 'history';
import { IArchiveSession } from 'interfaces/archive/IArchiveSession';
import { UploadStatus } from 'interfaces/session/IApiArchiveSessionModel';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { useInjection } from 'services/ioc/ioc.react';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { UiStore } from 'stores/UiStore';
import VideoUploaderStateStore from 'stores/VideoUploaderStateStore';
import { ArchivesWrapper, Explainer, Headline, LoadMore, TableContainer, Title } from './ArchivesStyles';
import BpTable from './components/BpTable';
import GamesTable from './components/GamesTable';

interface IProps {
  history: History;
}

const Archives: React.FC<IProps> = observer((props) => {
  const { history } = props;
  const [data, setData] = useState<IArchiveSession[]>([]);
  const [dataBp, setDataBp] = useState<IArchiveSession[]>([]);
  const [activeTab, setActiveTab] = useState<string>('games');
  const [areArchiveSessionsLoading, setAreArchiveSessionsLoading] = useState<boolean>(false);
  const [areBPArchiveSessionsLoading, setAreBPArchiveSessionsLoading] = useState<boolean>(false);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const videoUploaderStateStore = useInjection<VideoUploaderStateStore>(ServiceTypes.VideoUploaderStateStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  const loadArchiveSessions = async (): Promise<void> => {
    try {
      setAreArchiveSessionsLoading(true);
      await sessionStore.setArchiveSessions(0, sessionStore.archiveSessionsLoad);
    } catch (error: unknown) {
      if (error instanceof Error) {
        notificationStore.addNotification({
          id: 'archive-session-error',
          message: error.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            notificationStore.removeNotification('archive-session-error');
          },
        });
      }
    } finally {
      setAreArchiveSessionsLoading(false);
    }
  };

  const loadBPArchiveSessions = async (): Promise<void> => {
    try {
      setAreBPArchiveSessionsLoading(true);
      await sessionStore.setArchiveBattingPractices(0, sessionStore.archiveBattingPracticesLoad);
    } catch (error: unknown) {
      if (error instanceof Error) {
        notificationStore.addNotification({
          id: 'archive-bp-session-error',
          message: error.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            notificationStore.removeNotification('archive-bp-session-error');
          },
        });
      }
    } finally {
      setAreBPArchiveSessionsLoading(false);
    }
  };

  useEffect(() => {
    videoUploaderStateStore.setIsVisible(true);

    return (): void => {
      videoUploaderStateStore.setIsVisible(false);
    };
  }, []);

  useEffect(() => {
    loadArchiveSessions();
  }, [sessionStore.archiveSessionsLoad]);

  useEffect(() => {
    loadBPArchiveSessions();
  }, [sessionStore.archiveBattingPracticesLoad]);

  useEffect(() => {
    if (sessionStore.archiveSessions.some((session) => session.uploadState?.playVideoUploadStatus === UploadStatus.InProgress)) {
      return uiStore.setIsVideoUploaded(false);
    }

    uiStore.setIsVideoUploaded(true);

    return () => {
      uiStore.setIsVideoUploaded(true);
    };
  }, [data]);

  useEffect(() => {
    const mappedSessions = mappingHelpers.mapSessionsToArchiveSessions(sessionStore.archiveSessions, history);
    if (mappedSessions) {
      setData(mappedSessions);
    }
  }, [sessionStore.archiveSessions]);

  useEffect(() => {
    const mappedSessions = mappingHelpers.mapSessionsToArchiveSessions(sessionStore.archiveBattingPractices, history);
    if (mappedSessions) {
      setDataBp(mappedSessions);
    }
  }, [sessionStore.archiveBattingPractices]);

  return (
    <ArchivesWrapper>
      <Title>
        <Headline>Archives</Headline>
        <Explainer>Send, review and export previous games</Explainer>
      </Title>
      <TableContainer>
        <>
          <TabList onActiveTabChanged={setActiveTab}>
            <TabContent label='Games' name='games'>
              <GamesTable data={data} loading={areArchiveSessionsLoading || sessionStore.isLoading} />
            </TabContent>
            <TabContent label='Batting Practices' name='batting-practices'>
              <BpTable data={dataBp} loading={areBPArchiveSessionsLoading} />
            </TabContent>
          </TabList>
        </>
      </TableContainer>
      <LoadMore
        onClick={() => {
          if (activeTab === 'games') {
            sessionStore.archiveSessionsLoad += 30;
          } else {
            sessionStore.archiveBattingPracticesLoad += 30;
          }
        }}
      >
        load more
      </LoadMore>
    </ArchivesWrapper>
  );
});

export default Archives;
